import {
  HEALTH_ORIGIN_LABELS,
  type HealthOrigin,
  type HealthReviewRoute,
  type WellbeingPoint,
  type WellbeingSeries,
} from "@care-by-ella/contracts";
import {
  formatSymptomInstant,
  reviewRouteLabel,
  symptomSourceLabel,
} from "./symptom-lines";

// TES-1446 (Ö-654, Ö-675) — her "Mående" as the doctor reads it on the
// patient overview. The series is hers, served unchanged from the same
// service as "Din hälsa över tid"; these pure functions only word it and
// place its points, so both are testable without rendering.

/** One answer placed on the chart. */
export interface WellbeingChartPoint {
  point: WellbeingPoint;
  /**
   * `line`: an answer with a place on the scale, joined to the others.
   * `marker`: one without (a "Varierar"), drawn alone and never joined.
   */
  kind: "line" | "marker";
  /** Height from the top, 0–1; the first answer of the scale at the top. */
  y: number;
}

export interface WellbeingChart {
  /** The answers with a place on the scale, in the order she sees them. */
  levels: { value: string; label: string }[];
  /** Oldest first, as the series. */
  points: WellbeingChartPoint[];
}

/**
 * The same placement rule as the app's chart: the answers that have a number
 * are spaced as categories in the order she sees them (never by the number,
 * which for some questions is not in that order), and an answer without one
 * is a marker in the middle. Contracts' answerChartPoints (TES-1450, #1585)
 * states this rule for every role's chart; switch to it once it is on main.
 */
export function wellbeingChart(series: WellbeingSeries): WellbeingChart {
  const levels = series.options
    .filter((o) => o.normalized !== null)
    .map((o) => ({ value: o.value, label: o.label.sv }));
  const step = levels.length > 1 ? 1 / (levels.length - 1) : 0;
  const points = series.points.map((point): WellbeingChartPoint => {
    const i = levels.findIndex((l) => l.value === point.value);
    return point.normalized === null || i < 0
      ? { point, kind: "marker", y: 0.5 }
      : { point, kind: "line", y: levels.length > 1 ? i * step : 0.5 };
  });
  return { levels, points };
}

/** Whether the series is drawn: an ordinal scale with two joined answers. */
export function drawsLine(series: WellbeingSeries): boolean {
  return (
    series.chart &&
    wellbeingChart(series).points.filter((p) => p.kind === "line").length >= 2
  );
}

const SYMPTOM_SURFACES: ReadonlySet<string> = new Set([
  "f04",
  "f05",
  "dose",
  "besvarsguide",
  "health",
]);

/** Where she gave the answer, in the doctor's words for the same surfaces. */
export function wellbeingOriginLabel(origin: HealthOrigin | null): string {
  if (origin === null) return "Okänd källa";
  return SYMPTOM_SURFACES.has(origin)
    ? symptomSourceLabel(origin)
    : HEALTH_ORIGIN_LABELS[origin].sv;
}

/** One answer's provenance: date, source and route. */
export function wellbeingProvenance(point: WellbeingPoint): string {
  const parts = [
    formatSymptomInstant(point.observedAt),
    wellbeingOriginLabel(point.origin),
  ];
  const route: HealthReviewRoute | null = point.reviewRoute;
  if (route) parts.push(reviewRouteLabel(route));
  return parts.join(" · ");
}

/** The screen-reader summary of a drawn series. */
export function wellbeingChartSummary(series: WellbeingSeries): string {
  const first = series.points[0];
  const last = series.points[series.points.length - 1];
  if (!first || !last) return `${series.question.sv}: inga svar.`;
  return `${series.question.sv}: ${series.points.length} svar, från ${first.label.sv} (${formatSymptomInstant(first.observedAt)}) till ${last.label.sv} (${formatSymptomInstant(last.observedAt)}).`;
}
